/*
 * Priority navigation for the desktop header.
 *
 * Above 1400px the inline menu stays on a single row; any items that don't
 * fit the available width are moved into a "•••" overflow menu (a normal
 * Dawn <header-menu> dropdown). Below 1400px the inline menu is hidden by CSS
 * and the hamburger drawer takes over, so this script does nothing there.
 *
 * This file is loaded as a plain, non-deferred <script> immediately after the
 * <nav class="header__inline-menu"> it operates on (see sections/header.liquid)
 * — never with `defer`. It must run before the browser paints that nav, so an
 * overflowing menu is collapsed into "•••" before it's ever shown full-width;
 * loading it deferred meant the full menu flashed on every page load before
 * quietly re-collapsing a moment later.
 */
(function () {
  const BREAKPOINT = 1400;
  const MORE_LABEL = window.__headerMoreLabel || 'Mai multe';

  function build(nav) {
    const list = nav.querySelector('.list-menu--inline');
    if (!list || list.dataset.priorityNav === 'on') return null;
    list.dataset.priorityNav = 'on';

    // Same scheme the theme editor uses for real submenus (menu_color_scheme),
    // so the dropdown gets an opaque background instead of the ancestor's.
    const scheme = nav.dataset.menuColorScheme || 'scheme-1';

    const moreItem = document.createElement('li');
    moreItem.className = 'header__more list-menu__item';
    moreItem.innerHTML =
      '<header-menu>' +
      '<details>' +
      '<summary class="header__menu-item list-menu__item link focus-inset" aria-expanded="false">' +
      '<span class="visually-hidden">' + MORE_LABEL + '</span>' +
      '<span class="header__more-dots" aria-hidden="true">•••</span>' +
      '</summary>' +
      '<ul class="header__submenu list-menu list-menu--disclosure color-' + scheme + ' gradient motion-reduce global-settings-popup" role="list"></ul>' +
      '</details>' +
      '</header-menu>';

    const moreList = moreItem.querySelector('ul');
    list.appendChild(moreItem);

    const realItems = Array.prototype.filter.call(list.children, function (li) {
      return li !== moreItem;
    });

    return { nav: nav, list: list, moreItem: moreItem, moreList: moreList, realItems: realItems };
  }

  function layout(ctx) {
    const list = ctx.list;
    const moreItem = ctx.moreItem;

    // Close the overflow dropdown before re-measuring.
    const details = moreItem.querySelector('details');
    if (details) details.removeAttribute('open');

    // Reset — every real item back on the top row, in original order.
    ctx.realItems.forEach(function (li) {
      list.insertBefore(li, moreItem);
    });
    moreItem.classList.remove('header__more--visible');

    if (window.innerWidth < BREAKPOINT) return;

    const available = ctx.nav.clientWidth;
    if (list.scrollWidth <= available) return;

    // Something overflows — reveal "•••" and push items into it from the end.
    moreItem.classList.add('header__more--visible');
    for (let i = ctx.realItems.length - 1; i >= 0; i--) {
      if (list.scrollWidth <= available) break;
      ctx.moreList.insertBefore(ctx.realItems[i], ctx.moreList.firstChild);
    }
  }

  function setup(navs) {
    const contexts = [];
    navs.forEach(function (nav) {
      const ctx = build(nav);
      if (ctx) contexts.push(ctx);
    });
    if (!contexts.length) return;

    let scheduled = false;
    function run() {
      scheduled = false;
      contexts.forEach(layout);
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(run);
    }

    // First pass — synchronous, before paint (see file header comment). The
    // row stays invisible (CSS) until reveal() below, so this pass — measured
    // in whatever font is active before the real one has swapped in — is
    // never actually shown even if it gets the item count wrong.
    run();

    let revealed = false;
    function reveal() {
      if (revealed) return;
      revealed = true;
      run();
      contexts.forEach(function (ctx) {
        ctx.list.classList.add('header__list-menu--ready');
      });
    }

    // Reveal once the real font has taken effect (font-display: swap can
    // change which items fit) — that's what document.fonts.ready actually
    // tells us, so it's the primary signal even if it takes a while on a slow
    // connection. "load" (everything finished) and a generous failsafe are
    // backstops for browsers/situations where fonts.ready never resolves —
    // not a race against it, or a slow font would reveal too early with the
    // wrong item count and then visibly jump once it does finish loading.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(reveal);
    }
    window.addEventListener('load', reveal);
    setTimeout(reveal, 1500);

    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);

    // Belt-and-suspenders: also react to the header box itself changing size
    // (zoom, devtools docking, sticky-header shrink) which don't always fire a
    // window "resize". Observe an ancestor, never the nav we mutate.
    if (typeof ResizeObserver === 'function') {
      const target = document.querySelector('.header-wrapper') || document.body;
      let lastWidth = target.clientWidth;
      const ro = new ResizeObserver(function () {
        if (target.clientWidth === lastWidth) return;
        lastWidth = target.clientWidth;
        schedule();
      });
      ro.observe(target);
    }
  }

  // Normally this script's own nav is the immediately preceding element in
  // the DOM — act on just that one, synchronously, right now.
  const thisScript = document.currentScript;
  const ownNav = thisScript && thisScript.previousElementSibling;

  if (ownNav && ownNav.classList && ownNav.classList.contains('header__inline-menu')) {
    setup([ownNav]);
  } else {
    // Fallback for an unexpected DOM position (e.g. a theme-editor re-render
    // that moved things around) — process whatever exists once it's ready.
    const runFallback = function () {
      setup(Array.prototype.slice.call(document.querySelectorAll('.header__inline-menu')));
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runFallback);
    } else {
      runFallback();
    }
  }
})();
